import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import usePermissions from "../../../Components/Hooks/usePermissions";

import {
  fetchAllCoupons,
  fetchCouponTableColumns,
  deleteCoupon,
  activateDeactivateCoupon,
  updateUserPreference,
} from "../../../slices/thunks";

const actionConfig = {
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit Coupon",
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete Coupon",
  },
};

const Coupons = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [columnModal, setColumnModal] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const permission = usePermissions(3, 23);

  const {
    coupons,
    loading,
    tableColumnNames,
  } = useSelector((state) => ({
    coupons: state.Coupon.coupons,
    tableColumnNames: state.Coupon.tableColumnNames,
    loading: state.Coupon.loading,
  }));

  const onAddCoupon = () => {
    navigate("/payment/coupon/add");
  };

  const onEditCoupon = (coupon) => {
    navigate(`/payment/coupon/edit/${coupon.ID}`);
  };

  const onActiveOrDeactivate = (coupon) => {
    dispatch(activateDeactivateCoupon({ ID: coupon.ID }));
  };

  const confirmDelete = () => {
    dispatch(deleteCoupon({ IDs: selectedIds }))
      .then(() => setSelectedIds([]));
    setDeleteModal(false);
  };

  const saveUserPreferences = async () => {
    const payload = {
      ColumnIDs: tableColumnNames.reduce((acc, col) => {
        acc[col.ID] = selectedColumns.includes(col.ID);
        return acc;
      }, {}),
    };

    setColumnModal(false);
    await dispatch(updateUserPreference(payload));
    await dispatch(fetchCouponTableColumns({ module: "COUPONS" }));
  };

  useEffect(() => {
    dispatch(fetchCouponTableColumns({ module: "COUPONS" }));
    dispatch(fetchAllCoupons());
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("Coupons")} />

        <Card>
          <CardHeader>
            <Row className="g-4">
              <Col className="col-sm-auto">
                <Button
                  className="btn btn-soft-danger me-2"
                  onClick={() => setDeleteModal(true)}
                  disabled={!selectedIds.length}
                >
                  <i className="ri-delete-bin-2-line" />
                </Button>

                {permission.CanAdd && (
                  <Button
                    color="secondary"
                    onClick={onAddCoupon}
                    disabled={loading}
                  >
                    <i className="ri-add-line me-1" />
                    {t("AddNew")}
                  </Button>
                )}
              </Col>

              <Col className="col-sm">
                <div className="d-flex justify-content-sm-end">
                  <Button
                    className="btn btn-soft"
                    onClick={() => setColumnModal(true)}
                  >
                    <i className="ri-filter-3-line" />
                  </Button>

                  <div className="search-box ms-2">
                    <Input
                      placeholder={t("Search")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="ri-search-line search-icon" />
                  </div>
                </div>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {loading ? (
              <div className="d-flex justify-content-center">
                <Spinner />
              </div>
            ) : (
              <CommonDataTable
                data={coupons}
                tableColumnNames={tableColumnNames}
                searchTerm={searchTerm}
                onEditClickHandler={onEditCoupon}
                onActiveOrDeactiveChange={onActiveOrDeactivate}
                setDeleteModal={setDeleteModal}
                setSelectedIds={setSelectedIds}
                actionConfig={actionConfig}
                permission={permission}
              />
            )}
          </CardBody>
        </Card>
      </Container>

      {/* Column selection */}
      <ColumnSelectionModal
        isOpen={columnModal}
        toggle={() => setColumnModal(!columnModal)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      {/* Delete modal */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={t("confirmDelete")}
      />
    </div>
  );
};

export default Coupons;